import { decode } from "@googlemaps/polyline-codec";

import {
  MAP_LAT_LNG_TUPLE_LENGTH,
  MIN_TRACKING_POINTS_FOR_POLYLINE,
  MINIMAL_STATIONS_COUNT_FOR_ROUTE_REBUILD,
} from "../../constants";
import {
  IStationRouteWaypointLocationDraft,
  IStationRouteWaypointSegmentOption,
  IStationRouteWaypointStationLike,
} from "../../models";
import { isStationRouteWaypointStation } from "../routing-waypoint";

const closestPointOnSegment = ({
  px,
  py,
  x1,
  y1,
  x2,
  y2,
}: {
  px: number;
  py: number;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}): { distanceSquared: number; t: number } => {
  const dx = x2 - x1;
  const dy = y2 - y1;

  if (dx === 0 && dy === 0) {
    const ddx = px - x1;
    const ddy = py - y1;
    return { distanceSquared: ddx * ddx + ddy * ddy, t: 0 };
  }

  const t = Math.max(
    0,
    Math.min(1, ((px - x1) * dx + (py - y1) * dy) / (dx * dx + dy * dy)),
  );
  const projX = x1 + t * dx;
  const projY = y1 + t * dy;
  const distX = px - projX;
  const distY = py - projY;
  return { distanceSquared: distX * distX + distY * distY, t };
};

const distanceToSegmentSquared = ({
  px,
  py,
  x1,
  y1,
  x2,
  y2,
}: {
  px: number;
  py: number;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}): number => closestPointOnSegment({ px, py, x1, y1, x2, y2 }).distanceSquared;

const decodeRoutePolylineLatLng = (
  routePolyline: string | undefined,
): [number, number][] => {
  if (!routePolyline) {
    return [];
  }

  try {
    return decode(routePolyline).filter(
      (point): point is [number, number] =>
        Array.isArray(point) &&
        point.length >= MAP_LAT_LNG_TUPLE_LENGTH &&
        Number.isFinite(point[0]) &&
        Number.isFinite(point[1]),
    );
  } catch {
    return [];
  }
};

const nearestPolylineProgress = (
  latitude: number,
  longitude: number,
  polyline: [number, number][],
): number => {
  let bestProgress = 0;
  let bestDistance = Number.POSITIVE_INFINITY;

  for (let i = 0; i < polyline.length - 1; i++) {
    const from = polyline[i];
    const to = polyline[i + 1];
    const closest = closestPointOnSegment({
      px: latitude,
      py: longitude,
      x1: from[0],
      y1: from[1],
      x2: to[0],
      y2: to[1],
    });

    if (closest.distanceSquared < bestDistance) {
      bestDistance = closest.distanceSquared;
      bestProgress = i + closest.t;
    }
  }

  return bestProgress;
};

/** Map a click onto the visit-order pair using road polyline, not station-to-station chords. */
const findInsertIndexAlongPolyline = ({
  stations,
  latitude,
  longitude,
  routePolyline,
}: {
  stations: IStationRouteWaypointStationLike[];
  latitude: number;
  longitude: number;
  routePolyline: string | undefined;
}): number | undefined => {
  const polyline = decodeRoutePolylineLatLng(routePolyline);

  if (
    polyline.length < MIN_TRACKING_POINTS_FOR_POLYLINE ||
    polyline.length <= stations.length
  ) {
    return undefined;
  }

  const stationProgress = stations.map(station => {
    const lat = station.locationInfo?.latitude;
    const lng = station.locationInfo?.longitude;

    if (lat === undefined || lng === undefined) {
      return 0;
    }

    return nearestPolylineProgress(lat, lng, polyline);
  });

  for (let i = 1; i < stationProgress.length; i++) {
    if (stationProgress[i] < stationProgress[i - 1]) {
      stationProgress[i] = stationProgress[i - 1];
    }
  }

  if (stationProgress.at(-1) === stationProgress[0]) {
    return undefined;
  }

  const clickProgress = nearestPolylineProgress(latitude, longitude, polyline);

  for (let i = 0; i < stations.length - 1; i++) {
    if (
      clickProgress >= stationProgress[i] &&
      clickProgress <= stationProgress[i + 1]
    ) {
      return i;
    }
  }

  if (clickProgress < stationProgress[0]) {
    return 0;
  }

  return Math.max(
    stations.length - MINIMAL_STATIONS_COUNT_FOR_ROUTE_REBUILD,
    0,
  );
};

/**
 * Index after which a new waypoint should be inserted (nearest visit-order pair).
 * Prefers the saved road polyline so a click on a later street is not snapped to a nearby chord.
 */
export const findStationRouteWaypointInsertIndex = ({
  stations,
  latitude,
  longitude,
  routePolyline,
}: {
  stations: IStationRouteWaypointStationLike[];
  latitude: number;
  longitude: number;
  routePolyline?: string;
}): number => {
  if (!stations.length) {
    return 0;
  }

  if (stations.length < MINIMAL_STATIONS_COUNT_FOR_ROUTE_REBUILD) {
    return 0;
  }

  const alongPolyline = findInsertIndexAlongPolyline({
    stations,
    latitude,
    longitude,
    routePolyline,
  });

  if (alongPolyline !== undefined) {
    return alongPolyline;
  }

  let bestIndex = 0;
  let bestDistance = Number.POSITIVE_INFINITY;

  for (let i = 0; i < stations.length - 1; i++) {
    const from = stations[i]?.locationInfo;
    const to = stations[i + 1]?.locationInfo;
    const x1 = from?.latitude;
    const y1 = from?.longitude;
    const x2 = to?.latitude;
    const y2 = to?.longitude;

    if (
      x1 === undefined ||
      y1 === undefined ||
      x2 === undefined ||
      y2 === undefined
    ) {
      continue;
    }

    const segmentDistance = distanceToSegmentSquared({
      px: latitude,
      py: longitude,
      x1,
      y1,
      x2,
      y2,
    });

    if (segmentDistance < bestDistance) {
      bestDistance = segmentDistance;
      bestIndex = i;
    }
  }

  return bestIndex;
};

export const getStationRouteWaypointSegmentOptions = (
  stations: IStationRouteWaypointStationLike[],
): IStationRouteWaypointSegmentOption[] => {
  const realStationIndices = stations
    .map((station, index) =>
      isStationRouteWaypointStation(station) ? -1 : index,
    )
    .filter(index => index >= 0);

  const segmentOptions: IStationRouteWaypointSegmentOption[] = [];

  for (let i = 0; i < realStationIndices.length - 1; i++) {
    const fromIndex = realStationIndices[i];
    const toIndex = realStationIndices[i + 1];
    const fromStation = stations[fromIndex];
    const toStation = stations[toIndex];

    if (!fromStation || !toStation) {
      continue;
    }

    segmentOptions.push({
      insertAfterIndex: fromIndex,
      fromName: fromStation.name,
      toName: toStation.name,
    });
  }

  return segmentOptions;
};

/** Radio "between real stations" value for a precise insert-after index (may sit on a waypoint). */
export const getContainingRealStationSegmentInsertAfterIndex = ({
  stations,
  insertAfterIndex,
}: {
  stations: IStationRouteWaypointStationLike[];
  insertAfterIndex: number;
}): number => {
  const segmentOptions = getStationRouteWaypointSegmentOptions(stations);

  if (!segmentOptions.length) {
    return insertAfterIndex;
  }

  for (let i = Math.min(insertAfterIndex, stations.length - 1); i >= 0; i--) {
    if (segmentOptions.some(option => option.insertAfterIndex === i)) {
      return i;
    }
  }

  return segmentOptions[0]?.insertAfterIndex ?? insertAfterIndex;
};

export const resolveStationRouteWaypointInsertAfterIndex = ({
  stations,
  latitude,
  longitude,
  routePolyline,
}: {
  stations: IStationRouteWaypointStationLike[];
  latitude: number;
  longitude: number;
  routePolyline?: string;
}): number =>
  findStationRouteWaypointInsertIndex({
    stations,
    latitude,
    longitude,
    routePolyline,
  });

export const enrichStationRouteWaypointLocationDraft = ({
  draft,
  stations,
  routePolyline,
}: {
  draft: IStationRouteWaypointLocationDraft;
  stations: IStationRouteWaypointStationLike[];
  routePolyline?: string;
}): IStationRouteWaypointLocationDraft => ({
  ...draft,
  insertAfterIndex:
    draft.insertAfterIndex ??
    resolveStationRouteWaypointInsertAfterIndex({
      stations,
      latitude: draft.latitude,
      longitude: draft.longitude,
      routePolyline,
    }),
});

export const resolveStationRouteWaypointName = ({
  customName,
  fullAddress,
  fallbackName,
}: {
  customName?: string;
  fullAddress?: string;
  fallbackName: string;
}): string => {
  const trimmedCustomName = customName?.trim();
  if (trimmedCustomName) {
    return trimmedCustomName;
  }

  const trimmedFullAddress = fullAddress?.trim();
  if (trimmedFullAddress) {
    return trimmedFullAddress;
  }

  return fallbackName;
};
